/**
 * iter-438k · Bookshop Collection QR — till-side scanner.
 *
 * Staff at the counter can either:
 *   1. Use a USB barcode scanner (which pastes the envelope + Enter), OR
 *   2. Toggle the camera scanner to point the tablet at the customer's phone.
 *
 * On success the order flips to paid, entitlements are granted, and the QR
 * is consumed one-shot.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Html5Qrcode } from 'html5-qrcode';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { QrCode, CheckCircle2, XCircle, Loader2, Camera, CameraOff } from 'lucide-react';

const CAMERA_ELEMENT_ID = 'fwb-collection-qr-camera';

export const CollectionQRScan = () => {
  const [envelope, setEnvelope] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const inputRef = useRef(null);
  const scannerRef = useRef(null);
  // Debounce so a QR read doesn't spam the API if the frame stays in view.
  const lastScanRef = useRef({ text: '', at: 0 });

  const redeem = useMutation({
    mutationFn: async (env) => (await api.post('/bookshop/collection/qr/redeem', {
      envelope: env, device: navigator.userAgent?.slice(0, 120) || 'unknown',
    })).data,
    onSuccess: (res) => {
      setLastResult(res);
      if (res.success) {
        toast.success(
          res.already_paid
            ? `Order ${res.order_number} was already paid`
            : `Order ${res.order_number} — ${res.granted || 0} item(s) unlocked`,
        );
      } else {
        toast.error(res.reason || 'Invalid QR');
      }
      setEnvelope('');
      if (!cameraOn) setTimeout(() => inputRef.current?.focus(), 100);
    },
    onError: (e) => {
      setLastResult({ success: false, reason: getErrorMessage(e) });
      toast.error(getErrorMessage(e));
    },
  });

  const handleDecoded = useCallback((decodedText) => {
    if (!decodedText) return;
    const now = Date.now();
    if (lastScanRef.current.text === decodedText && now - lastScanRef.current.at < 3000) return;
    lastScanRef.current = { text: decodedText, at: now };
    // Only submit envelopes that look like ours — silently ignore other QRs
    if (!/^FWM:v\d+:rcpt:/.test(decodedText)) return;
    setEnvelope(decodedText);
    redeem.mutate(decodedText);
  }, [redeem]);

  const stopCamera = useCallback(async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      if (s.isScanning) await s.stop();
      await s.clear();
    } catch {
      /* html5-qrcode throws harmless errors if the element was already torn down */
    } finally {
      scannerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!cameraOn) { stopCamera(); return; }
    let cancelled = false;
    (async () => {
      try {
        setCameraError('');
        const html5 = new Html5Qrcode(CAMERA_ELEMENT_ID, { verbose: false });
        scannerRef.current = html5;
        // Prefer the rear (environment) camera on tablets/phones.
        await html5.start(
          { facingMode: { ideal: 'environment' } },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          handleDecoded,
          () => { /* ignore per-frame decode misses */ },
        );
        if (cancelled) await stopCamera();
      } catch (e) {
        // iter-438k: keep cameraOn=true so the error surface stays visible.
        // User must click the toggle again to hide it. Without this the
        // panel unmounted instantly and users saw zero feedback in the
        // denied-permission path.
        setCameraError(e?.message || 'Camera unavailable — check browser permissions');
      }
    })();
    return () => { cancelled = true; stopCamera(); };
  }, [cameraOn, handleDecoded, stopCamera]);

  const submit = () => {
    const env = envelope.trim();
    if (!env) return;
    redeem.mutate(env);
  };

  return (
    <div className="space-y-3" data-testid="bookshop-collection-qr-scan">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <QrCode className="h-4 w-4 text-emerald-600" /> Till scanner — scan collection QR
          </CardTitle>
          <CardDescription className="text-xs">
            Use a USB barcode scanner and hit Enter, paste the envelope, or flip on the camera to scan the customer&apos;s phone directly.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              autoFocus={!cameraOn}
              placeholder="FWM:v1:rcpt:… (paste or scan)"
              value={envelope}
              onChange={(e) => setEnvelope(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              className="font-mono text-xs"
              data-testid="collection-qr-envelope-input"
            />
            <Button
              onClick={submit}
              disabled={!envelope.trim() || redeem.isPending}
              data-testid="collection-qr-redeem-btn"
            >
              {redeem.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Redeem'}
            </Button>
            <Button
              variant={cameraOn ? 'destructive' : 'outline'}
              onClick={() => setCameraOn((v) => !v)}
              data-testid="collection-qr-camera-toggle"
              title={cameraOn ? 'Stop camera' : 'Scan with camera'}
            >
              {cameraOn ? <CameraOff className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
            </Button>
          </div>

          {cameraOn && (
            <div className="rounded border border-slate-200 bg-black/5 p-2" data-testid="collection-qr-camera-panel">
              <div
                id={CAMERA_ELEMENT_ID}
                className="mx-auto"
                style={{ width: '100%', maxWidth: 360, aspectRatio: '1 / 1' }}
                data-testid="collection-qr-camera-view"
              />
              <p className="text-[11px] text-slate-500 text-center mt-2">
                Point the rear camera at the customer&apos;s QR. Scans auto-submit.
              </p>
              {cameraError && (
                <p className="text-[11px] text-red-600 text-center mt-1" data-testid="collection-qr-camera-error">
                  {cameraError}
                </p>
              )}
            </div>
          )}

          {lastResult && (
            <div
              className={`rounded border p-3 text-sm ${lastResult.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
              data-testid="collection-qr-last-result"
            >
              <div className="flex items-center gap-2 font-medium">
                {lastResult.success
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  : <XCircle className="h-4 w-4 text-red-600" />}
                <span data-testid="collection-qr-verdict">
                  {lastResult.success
                    ? (lastResult.already_paid ? 'Already paid' : 'Payment confirmed')
                    : (lastResult.reason || 'Rejected')}
                </span>
              </div>
              {lastResult.order_number && (
                <div className="mt-1 text-xs text-slate-600 space-y-0.5">
                  <div>Order: <strong data-testid="collection-qr-order-number">{lastResult.order_number}</strong></div>
                  {lastResult.customer_name && <div>Customer: {lastResult.customer_name} {lastResult.customer_email && `· ${lastResult.customer_email}`}</div>}
                  {lastResult.total != null && (
                    <div>
                      Total: {lastResult.currency || 'GBP'} {Number(lastResult.total).toFixed(2)}
                      {' '} <Badge variant="outline" className="text-[10px]" data-testid="collection-qr-granted">{lastResult.granted || 0} item(s) unlocked</Badge>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CollectionQRScan;
